import { useMemo, useState, useEffect } from "react";
import { FaSquareMinus, FaSquarePlus } from "react-icons/fa6";

export default function RolesTreeStructure({ treeMenu = [], setSelectedRoles }) {
    const [data, setData] = useState(treeMenu);
    const [expanded, setExpanded] = useState(new Set());
    const [checked, setChecked] = useState(() => new Set());
    const [delegatedChecked, setDelegatedChecked] = useState(() => new Set());

    const toId = (v) => Number(v);

    const buildSelectedRoles = (checkedSet, delegatedSet, menuData) => {
        return menuData
            .filter(item => checkedSet.has(toId(item.menu_id)))
            .map(item => ({
                ...item,
                menu_id: toId(item.menu_id),
                can_be_delegated: delegatedSet.has(toId(item.menu_id)) ? "Y" : "N",
            }));
    };

    useEffect(() => {
        setData(treeMenu);

        const nextChecked = new Set(
            treeMenu.filter(item => item.showMenu).map(item => toId(item.menu_id))
        );
        const nextDelegated = new Set(
            treeMenu
                .filter(item => item.can_be_delegated === true || item.can_be_delegated === "true" || item.can_be_delegated === "Y")
                .map(item => toId(item.menu_id))
        );

        setChecked(nextChecked);
        setDelegatedChecked(nextDelegated);
        setSelectedRoles(buildSelectedRoles(nextChecked, nextDelegated, treeMenu));

        setExpanded(new Set(treeMenu.map(item => toId(item.menu_id))));
    }, [treeMenu]);

    const tree = useMemo(() => {
        const map = {};
        const roots = [];

        data.forEach((item) => {
            const id = toId(item.menu_id);
            map[id] = { ...item, menu_id: id, children: [] };
        });

        data.forEach((item) => {
            const id = toId(item.menu_id);
            const parentId = toId(item.parent_menu_id);
            if (!parentId || parentId === 0 || !map[parentId]) {
                roots.push(map[id]);
            } else {
                map[parentId]?.children.push(map[id]);
            }
        });

        return roots;
    }, [data]);

    const toggleExpand = (id) => {
        const key = toId(id);
        const temp = new Set(expanded);
        if (temp.has(key)) temp.delete(key);
        else temp.add(key);
        setExpanded(temp);
    };

    const getDescendants = (id) => {
        const result = [];
        const dfs = (parentId) => {
            data.forEach((item) => {
                if (toId(item.parent_menu_id) === toId(parentId)) {
                    const childId = toId(item.menu_id);
                    result.push(childId);
                    dfs(childId);
                }
            });
        };
        dfs(id);
        return result;
    };

    // When checking a child, keep/add parents so hierarchy stays assigned.
    // When unchecking, do NOT auto-remove parents — only remove what user unchecked.
    const ensureAncestorsChecked = (id, checkedSet) => {
        const current = data.find((x) => toId(x.menu_id) === toId(id));
        if (!current) return;
        const parentId = toId(current.parent_menu_id);
        if (!parentId || parentId === 0) return;
        checkedSet.add(parentId);
        ensureAncestorsChecked(parentId, checkedSet);
    };

    const toggleCheck = (id) => {
        const key = toId(id);
        const temp = new Set(checked);
        const tempDelegated = new Set(delegatedChecked);

        if (temp.has(key)) {
            // Uncheck only this node + its submenus
            temp.delete(key);
            getDescendants(key).forEach((child) => {
                temp.delete(child);
                tempDelegated.delete(child);
            });
            tempDelegated.delete(key);
        } else {
            temp.add(key);
            getDescendants(key).forEach((child) => temp.add(child));
            ensureAncestorsChecked(key, temp);
        }

        setChecked(temp);
        setDelegatedChecked(tempDelegated);
        setSelectedRoles(buildSelectedRoles(temp, tempDelegated, data));
    };

    const toggleDelegatedCheck = (id) => {
        const key = toId(id);
        const temp = new Set(delegatedChecked);

        if (temp.has(key)) {
            // Uncheck delegate only for this node + its submenus (menu permission stays)
            temp.delete(key);
            getDescendants(key).forEach((child) => temp.delete(child));
        } else {
            temp.add(key);
            getDescendants(key).forEach((child) => temp.add(child));
            ensureAncestorsChecked(key, temp);
        }

        setDelegatedChecked(temp);
        setSelectedRoles(buildSelectedRoles(checked, temp, data));
    };

    const renderNode = (node, level = 0, isLast = false) => {
        const nodeId = toId(node.menu_id);
        return (
            <div key={nodeId}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 8px",
                        marginLeft: level * 22,
                        borderRadius: 6,
                        transition: "0.2s",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f5f7fa";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                    }}
                >
                    {level > 0 && (
                        <div
                            style={{
                                width: 12,
                                color: "#999",
                                fontFamily: "monospace",
                            }}
                        >
                            {isLast ? "└" : "├"}
                        </div>
                    )}

                    {node.children?.length > 0 ? (
                        <button
                            onClick={() => toggleExpand(nodeId)}
                            style={{
                                width: 18,
                                height: 18,
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                fontSize: 12,
                                color: "#666",
                                marginBottom: 6,
                                marginRight: 10
                            }}
                        >
                            {expanded.has(nodeId) ?
                                <FaSquareMinus size={20} color="#fc6203" /> :
                                <FaSquarePlus size={20} color="#fc6203" />
                            }
                        </button>
                    ) : (
                        <div style={{ width: 18 }} />
                    )}
                    <input
                        type="checkbox"
                        checked={checked.has(nodeId)}
                        onChange={() => toggleCheck(nodeId)}
                    />

                    <span
                        style={{
                            fontWeight: node.children?.length ? 600 : 400,
                            color: "#333",
                            flex: 1
                        }}
                    >
                        {node.menu_name}
                    </span>

                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 13,
                            color: "#555"
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={delegatedChecked.has(nodeId)}
                            onChange={() => toggleDelegatedCheck(nodeId)}
                            disabled={!checked.has(nodeId)}
                        />
                        Can be delegated
                    </label>
                </div>

                {expanded.has(nodeId) &&
                    node.children?.map((child, index) =>
                        renderNode(
                            child,
                            level + 1,
                            index === node.children.length - 1
                        )
                    )}
            </div>
        );
    };

    return (
        <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, background: "#fff", width: "100%" }}>
            {tree.length > 0 ? (
                tree.map((node, index) =>
                    renderNode(node, 0, index === tree.length - 1)
                )
            ) : (
                <div style={{ textAlign: "center", color: "#999", padding: "20px 0" }}>
                    No roles found
                </div>
            )}
        </div>
    );
}
